import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PasswordStrengthMeter from '../../components/admin/PasswordStrengthMeter';
import { isPasswordStrong } from '../../utils/passwordStrength';

const ROLES = [
    { value: 'admin', label: 'Admin' },
    { value: 'manager', label: 'Manager' },
    { value: 'staff', label: 'Staff' },
];

const emptyUserForm = {
    username: '',
    email: '',
    password: '',
    role: 'staff',
    isActive: true,
};

const AdminSecurity = () => {
    const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

    const currentUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('adminUser') || '{}');
        } catch {
            return {};
        }
    }, []);

    const isAdmin = currentUser.role === 'admin';

    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [passwordMsg, setPasswordMsg] = useState({ type: '', text: '' });
    const [changingPassword, setChangingPassword] = useState(false);

    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [usersError, setUsersError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [userForm, setUserForm] = useState(emptyUserForm);
    const [savingUser, setSavingUser] = useState(false);
    const [resetTarget, setResetTarget] = useState(null);
    const [resetPassword, setResetPassword] = useState('');
    const [resetConfirm, setResetConfirm] = useState('');

    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('adminToken');
        return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        };
    }, []);

    const loadUsers = useCallback(async () => {
        if (!isAdmin) return;
        setLoadingUsers(true);
        setUsersError('');
        try {
            const response = await fetch(`${API_URL}/api/admin/admins`, {
                headers: getAuthHeaders(),
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.detail || 'Failed to load users');
            }
            const data = await response.json();
            setUsers(data);
        } catch (error) {
            setUsersError(error.message || 'Failed to load users');
        } finally {
            setLoadingUsers(false);
        }
    }, [API_URL, getAuthHeaders, isAdmin]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordMsg({ type: '', text: '' });

        if (!isPasswordStrong(passwordForm.newPassword)) {
            setPasswordMsg({ type: 'error', text: 'Please choose a stronger password that meets all requirements' });
            return;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setPasswordMsg({ type: 'error', text: 'New passwords do not match' });
            return;
        }

        setChangingPassword(true);
        try {
            const response = await fetch(`${API_URL}/api/auth/change-password`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    currentPassword: passwordForm.currentPassword,
                    newPassword: passwordForm.newPassword,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to change password');
            }
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setPasswordMsg({ type: 'success', text: 'Password changed successfully' });
            const stored = JSON.parse(localStorage.getItem('adminUser') || '{}');
            localStorage.setItem('adminUser', JSON.stringify({ ...stored, mustChangePassword: false }));
        } catch (error) {
            setPasswordMsg({ type: 'error', text: error.message || 'Failed to change password' });
        } finally {
            setChangingPassword(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!isPasswordStrong(userForm.password)) {
            alert('Please choose a stronger password that meets all requirements');
            return;
        }

        setSavingUser(true);
        try {
            const response = await fetch(`${API_URL}/api/admin/admins`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(userForm),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to create user');
            }
            setShowCreateModal(false);
            setUserForm(emptyUserForm);
            await loadUsers();
            alert('User created successfully');
        } catch (error) {
            alert(error.message || 'Failed to create user');
        } finally {
            setSavingUser(false);
        }
    };

    const toggleUserActive = async (user) => {
        if (user.id === currentUser.id) {
            alert('You cannot deactivate your own account');
            return;
        }
        const nextActive = !user.isActive;
        const action = nextActive ? 'activate' : 'deactivate';
        if (!window.confirm(`Are you sure you want to ${action} ${user.username}?`)) return;

        try {
            const response = await fetch(`${API_URL}/api/admin/admins/${user.id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ isActive: nextActive }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.detail || `Failed to ${action} user`);
            }
            await loadUsers();
        } catch (error) {
            alert(error.message || `Failed to ${action} user`);
        }
    };

    const updateUserRole = async (user, role) => {
        if (user.id === currentUser.id && role !== 'admin') {
            alert('You cannot remove your own admin role');
            return;
        }
        try {
            const response = await fetch(`${API_URL}/api/admin/admins/${user.id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ role }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to update role');
            }
            await loadUsers();
        } catch (error) {
            alert(error.message || 'Failed to update role');
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!resetTarget) return;
        if (!isPasswordStrong(resetPassword)) {
            alert('Please choose a stronger password that meets all requirements');
            return;
        }
        if (resetPassword !== resetConfirm) {
            alert('Passwords do not match');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/api/admin/admins/${resetTarget.id}/reset-password`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ newPassword: resetPassword }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to reset password');
            }
            setResetTarget(null);
            setResetPassword('');
            setResetConfirm('');
            alert(`Password reset for ${resetTarget.username}`);
        } catch (error) {
            alert(error.message || 'Failed to reset password');
        }
    };

    const deleteUser = async (user) => {
        if (user.id === currentUser.id) {
            alert('You cannot delete your own account');
            return;
        }
        if (!window.confirm(`Delete user "${user.username}"? This cannot be undone.`)) return;

        try {
            const response = await fetch(`${API_URL}/api/admin/admins/${user.id}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to delete user');
            }
            await loadUsers();
        } catch (error) {
            alert(error.message || 'Failed to delete user');
        }
    };

    return (
        <div>
            <h2 className="text-3xl font-bold mb-6">Security</h2>

            {/* Change password */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
                <h3 className="text-xl font-bold mb-2">Change Password</h3>
                <p className="text-gray-500 text-sm mb-6">
                    Update the password for your account ({currentUser.email || 'current user'}).
                </p>

                <form onSubmit={handleChangePassword} className="max-w-md space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                        <input
                            type="password"
                            required
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                        <input
                            type="password"
                            required
                            minLength={8}
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <PasswordStrengthMeter password={passwordForm.newPassword} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                        <input
                            type="password"
                            required
                            minLength={8}
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {passwordMsg.text && (
                        <div
                            className={`px-4 py-3 rounded ${
                                passwordMsg.type === 'success'
                                    ? 'bg-green-100 text-green-700 border border-green-300'
                                    : 'bg-red-100 text-red-700 border border-red-300'
                            }`}
                        >
                            {passwordMsg.text}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={changingPassword || !isPasswordStrong(passwordForm.newPassword)}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                        {changingPassword ? 'Updating...' : 'Update Password'}
                    </button>
                </form>
            </div>

            {/* User management */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <h3 className="text-xl font-bold">Users</h3>
                        <p className="text-gray-500 text-sm">
                            Create admin users and manage roles, access, and password resets.
                        </p>
                    </div>
                    {isAdmin && (
                        <button
                            onClick={() => {
                                setUserForm(emptyUserForm);
                                setShowCreateModal(true);
                            }}
                            className="bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700"
                        >
                            <i className="fas fa-user-plus mr-2"></i>Create User
                        </button>
                    )}
                </div>

                {!isAdmin ? (
                    <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
                        Only admins can manage users. Contact an admin if you need access changes.
                    </div>
                ) : loadingUsers ? (
                    <div className="text-center py-8 text-gray-500">Loading users...</div>
                ) : usersError ? (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{usersError}</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td className="px-4 py-4">
                                            <div className="font-medium text-gray-900">{user.username}</div>
                                            <div className="text-sm text-gray-500">{user.email}</div>
                                            {user.id === currentUser.id && (
                                                <span className="text-xs text-blue-600">You</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            <select
                                                value={user.role}
                                                onChange={(e) => updateUserRole(user, e.target.value)}
                                                className="border border-gray-300 rounded px-2 py-1 text-sm"
                                            >
                                                {ROLES.map((role) => (
                                                    <option key={role.value} value={role.value}>
                                                        {role.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-4 py-4 space-y-1">
                                            <span
                                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                    user.isActive
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-600'
                                                }`}
                                            >
                                                {user.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                            {user.mustChangePassword && (
                                                <span className="block text-xs text-amber-600">
                                                    Must change password
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 text-right space-x-2 whitespace-nowrap">
                                            <button
                                                onClick={() => {
                                                    setResetTarget(user);
                                                    setResetPassword('');
                                                    setResetConfirm('');
                                                }}
                                                className="text-blue-600 hover:text-blue-800 text-sm"
                                                title="Reset password"
                                            >
                                                <i className="fas fa-key mr-1"></i>Reset
                                            </button>
                                            <button
                                                onClick={() => toggleUserActive(user)}
                                                className="text-yellow-600 hover:text-yellow-800 text-sm"
                                                title={user.isActive ? 'Deactivate' : 'Activate'}
                                            >
                                                <i className={`fas ${user.isActive ? 'fa-user-slash' : 'fa-user-check'} mr-1`}></i>
                                                {user.isActive ? 'Deactivate' : 'Activate'}
                                            </button>
                                            <button
                                                onClick={() => deleteUser(user)}
                                                className="text-red-600 hover:text-red-800 text-sm"
                                                title="Delete user"
                                            >
                                                <i className="fas fa-trash mr-1"></i>Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {users.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                                            No users found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create user modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">Create User</h3>
                            <button onClick={() => setShowCreateModal(false)} className="text-gray-500 hover:text-gray-700">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleCreateUser} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Username</label>
                                <input
                                    required
                                    value={userForm.username}
                                    onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Email</label>
                                <input
                                    type="email"
                                    required
                                    value={userForm.email}
                                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Temporary Password</label>
                                <input
                                    type="password"
                                    required
                                    minLength={8}
                                    value={userForm.password}
                                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                />
                                <PasswordStrengthMeter password={userForm.password} />
                                <p className="text-xs text-gray-500 mt-2">
                                    User will be asked to change this password on first login.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Role</label>
                                <select
                                    value={userForm.role}
                                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                >
                                    {ROLES.map((role) => (
                                        <option key={role.value} value={role.value}>
                                            {role.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={userForm.isActive}
                                    onChange={(e) => setUserForm({ ...userForm, isActive: e.target.checked })}
                                />
                                Active account
                            </label>
                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="px-4 py-2 rounded-lg border border-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={savingUser || !isPasswordStrong(userForm.password)}
                                    className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                >
                                    {savingUser ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reset password modal */}
            {resetTarget && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">Reset Password</h3>
                            <button onClick={() => setResetTarget(null)} className="text-gray-500 hover:text-gray-700">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            Set a temporary password for <strong>{resetTarget.username}</strong> ({resetTarget.email}).
                            They will be required to change it on next login.
                        </p>
                        <form onSubmit={handleResetPassword} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">New Password</label>
                                <input
                                    type="password"
                                    required
                                    minLength={8}
                                    value={resetPassword}
                                    onChange={(e) => setResetPassword(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                />
                                <PasswordStrengthMeter password={resetPassword} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Confirm Password</label>
                                <input
                                    type="password"
                                    required
                                    minLength={8}
                                    value={resetConfirm}
                                    onChange={(e) => setResetConfirm(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                />
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setResetTarget(null)}
                                    className="px-4 py-2 rounded-lg border border-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!isPasswordStrong(resetPassword)}
                                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                >
                                    Reset Password
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSecurity;
